/** @jsx jsx */
import { FiltersIcon } from "@eworkit/iris";
import React, { FC, useCallback, useContext } from "react";
import { jsx } from "theme-ui";

import { Group } from "@/components/Group";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { fetchJobRequestFilters, putJobRequestFilter, TEmailNotificationFrequency } from "@/data/job-requests";
import { Flex } from "@/eworkitComponents";
import { useFetcher } from "@/hooks/http";
import { CodelistGenericSelect, ICodelistGenericSelectParams } from "@/views/components/Codelists";

const EMAIL_NOTIFICATION_FREQUENCIES_CODELIST_QUERY: ICodelistGenericSelectParams = {
  codelistName: "filter-email-frequency",
};

interface ICustomFiltersSettingsProps {}

export const CustomFiltersSettings: FC<ICustomFiltersSettingsProps> = () => {
  const User = useContext(UserContext);
  const companyId = User.contextCompany?.id;

  const filtersFetchState = useFetcher(fetchJobRequestFilters, { companyId }, [companyId]);

  const handleFilterSettingChange = useCallback(
    async (filterId: number, value: TEmailNotificationFrequency) => {
      try {
        if (filtersFetchState.payload) {
          const filtersResponse = await Promise.all(
            filtersFetchState.payload.map(filter => {
              if (filter.id === filterId && filter.frequency !== value) {
                return putJobRequestFilter({ id: filterId, companyId }, { ...filter, frequency: value });
              }

              return Promise.resolve(filter);
            }),
          );
          filtersFetchState.updatePayload(filtersResponse);
        }
      } catch (error) {
        // TODO: complete catch once it's specified
      }
    },
    [filtersFetchState, companyId],
  );

  if (filtersFetchState.payload && !filtersFetchState.payload.length) {
    return null;
  }

  return (
    <React.Fragment>
      <Flex direction="column" innerBorder innerPadding="sm">
        {filtersFetchState.payload &&
          filtersFetchState.payload.map(filter => (
            <div className="email-notification-setting" key={filter.id}>
              <Flex direction="row" align="center">
                <Flex direction="row" width={9} align="flex-start">
                  <FiltersIcon size="md" color="n700" sx={{ marginRight: "xs" }} />
                  <Text size="md" color="black">
                    {filter.name}
                  </Text>
                </Flex>

                <Flex justify="flex-end" direction="row" width={3}>
                  <Group>
                    <CodelistGenericSelect
                      name="frequency"
                      onChange={value => handleFilterSettingChange(filter.id, value)}
                      query={EMAIL_NOTIFICATION_FREQUENCIES_CODELIST_QUERY}
                      required
                      simpleValue
                      testName="frequency"
                      value={filter.frequency}
                      size="md"
                      isClearable={false}
                    />
                  </Group>
                </Flex>
              </Flex>
            </div>
          ))}
      </Flex>
    </React.Fragment>
  );
};
